import React from 'react';
import { Route, Redirect, Switch } from 'react-router';
import { push } from 'connected-react-router';
import _ from 'lodash';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import axios from 'global/axios-instance';
import { updateSession, populateSettings } from 'global/actions/global-actions';
import { bindActionCreators } from 'redux';
import CircularProgress from '@material-ui/core/CircularProgress';
import Login from 'anonymous/views/login';
import AccessError from './signed-in/views/access-error';
import AccountRequest from './signed-in/views/account-request';

const anonymousRoutes = [];

const signedInRoutes = [
    {
        path: '/',
        component: React.lazy(() => import('signed-in/views/signed-in-root'))
    }
];

function mapStateToProps(state) {
    return {
        token: state.session.token,
        user: state.session.user
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        updateSession,
        populateSettings,
        push
    }, dispatch);
}

function LoginRedirectRoute({ component: Component, ...rest }) {
    return (
        <Route
            {...rest}
            render={props =>
                <Redirect
                    to={{
                        pathname: '/login',
                        state: { from: props.location }
                    }}
                />
            }
        />
    );
}

LoginRedirectRoute.prototype.propTypes = {
    component: PropTypes.node,
    location: PropTypes.object.isRequired
};

@connect(mapStateToProps, mapDispatchToProps)
class RootRoutes extends React.PureComponent {
    static propTypes = {
        token: PropTypes.string,
        user: PropTypes.object,
        updateSession: PropTypes.func.isRequired,
        populateSettings: PropTypes.func.isRequired,
        push: PropTypes.func.isRequired
    };

    state = {
        sessionReady: false
    };

    constructor(props) {
        super(props);

        this.authenticatedRoutes = this.buildRoutes(true, false);
        this.notAuthenticatedRoutes = this.buildRoutes(false, false);
    }

    async componentDidMount() {
        const token = localStorage.jwt;

        const settings = (await axios.get('/public/settings')).data;
        await this.props.populateSettings(settings);

        if (token) {
            await this.props.updateSession({ token });

            const result = await axios.get('/auth/session');

            await this.props.updateSession({ user: result.data });
        } else {
            this.routes = this.buildRoutes(false);
            this.setState({ sessionReady: true });
        }
    }

    async componentDidUpdate(prevProps, prevState) {
        if (_.isNil(prevProps.user) && !_.isNil(this.props.user)) {
            this.setState({ sessionReady: true });
        }
        /*if (!_.isNil(prevProps.user) && prevProps.user.isFirstLogin !== this.props.user.isFirstLogin) {
            const result = await axios.get('/auth/session');
            await this.props.updateSession({ user: result.data });
        }*/
    }

    buildRoutes(isAuthenticated) {
        const routes = [];
        routes.push(<Route path='/login' component={Login} key={-1} />);

        routes.push(<Route path='/access-pending' key={-2}>
            <Redirect to={'/'} />
        </Route>);

        routes.push(<Route path='/no-access' key={-2}>
            <Redirect to={'/'} />
        </Route>);

        routes.push(...anonymousRoutes.map((route, i) =>
            <Route exact path={route.path} key={i} component={route.component} />
        ));

        routes.push(...signedInRoutes.map((route, i) => {
            if (!isAuthenticated) {
                return <LoginRedirectRoute path={route.path} key={i} component={Login} />;
            } else {
                return (<Route path={route.path} key={i} component={route.component} />);
            }
        }));

        return routes;
    }

    render() {
        const { sessionReady } = this.state;
        const { user } = this.props;
        if (!sessionReady) {
            return <CircularProgress />;
        }
        let routes;

        // Early exit: user does not exist.
        if (_.isNil(user)) {
            return <Switch>{this.notAuthenticatedRoutes}</Switch>;
        }

        const getRedirectRoute = (url) => {
            return (<Route key={-2}><Redirect to={url} /></Route>);
        };

        if (!user.accessApproved) {
            switch (user.userRequestStatusName) {
                case 'DENIED':
                    routes = [
                        (<Route exact path='/account-request' component={AccountRequest} key={-3} />),
                        getRedirectRoute('/account-request')
                    ];
                    break;
                case 'OPEN (NEW USER)':
                    routes = [
                        (<Route path='/access-pending' key={-3}>
                            <AccessError>
                                <p>Your access request is pending. An administrator has been notified.</p>
                                <ContactHelp />
                            </AccessError>
                        </Route>),
                        getRedirectRoute('/access-pending')
                    ];
                    break;
                case 'APPROVED':
                    // Something has gone wrong on the backend, because if a
                    // user does not have access approved, they should not have
                    // an approved user creation request.
                    routes = [
                        (<Route path='/no-access' key={-3}>
                            <AccessError>
                                <p>There is a problem with registering your account.</p>
                                <ContactHelp />
                            </AccessError>
                        </Route>),
                        getRedirectRoute('/access-pending')
                    ];
                    break;
                default:
                    // default includes cases where the user has no user
                    // creation request.
                    routes = [
                        (<Route exact path='/account-request' component={AccountRequest} key={-3} />),
                        getRedirectRoute('/account-request')
                    ];
                    break;
            }
        } else if (_.isEmpty(user.awsRegions) && _.isEmpty(user.countries)) {
            /**
             * @todo: AUS-357 if the user gets into this state, the browser
             *        should throw an error because this state is supposed to be
             *        impossible. Make sure the UI is sending logs to Datadog
             *        RUM before creating correct error handling.
             */
            routes = [
                (<Route path='/no-access' key={-3}>
                    <AccessError>
                        <p>This account does not have access to jAUS.</p>
                        <ContactHelp />
                    </AccessError>
                </Route>),
                getRedirectRoute('/no-access')
            ];
        } else {
            routes = this.authenticatedRoutes;
        }
        return <Switch>{routes}</Switch>;
    }
}

const ContactHelp = () => {
    return (
        <p>Please contact <a href='mailto:labsdc_pdmteam@laerdal.com'>labsdc_pdmteam@laerdal.com</a> for additional
            assistance.</p>
    );
};

export default RootRoutes;
